/**
 * Video Service
 * Handles all video-related API calls
 */

const API_BASE_URL = `${process.env.REACT_APP_API_URL}/public`;

/**
 * Fetch single video by ID using dedicated endpoint
 * @param {string} videoId - The video ID to fetch
 * @returns {Promise<Object>} Full video object with all embed data
 */
export const fetchVideoByIdDirect = async (videoId) => {
  try {
    const url = `${API_BASE_URL}/videos/${videoId}`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const responseData = await response.json();
    
    // Handle wrapped response: { success, data: {...}, ... } or direct object
    const video = Array.isArray(responseData) ? responseData[0] : (responseData.data || responseData);
    
    if (!video || !video.id) {
      throw new Error(`Invalid video response for ID ${videoId}`);
    }
    
    return video;
  } catch (error) {
    throw error;
  }
};

/**
 * Find full video details by ID from the videos list endpoint
 * Uses the same list endpoint to fetch all videos, then searches for the specific video
 * Falls back to this if the direct endpoint fails
 * @param {string} videoId - The video ID to find
 * @param {Object} filterParams - Filter parameters for the API call (section, statut, year, month, etc.)
 * @returns {Promise<Object>} Full video object with all embed data
 */
export const fetchVideoById = async (videoId, filterParams = {}) => {
  try {
    // Use the list endpoint with filters to get all videos
    const params = {
      limit: 100, // Get more results to find the video
      ...filterParams,
    };
    
    const queryString = new URLSearchParams(params).toString();
    const url = `${API_BASE_URL}/videos?${queryString}`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const responseData = await response.json();
    
    // Handle wrapped response: { success, data: [...], pagination: {...} }
    const videos = Array.isArray(responseData) ? responseData : (responseData.data || []);
    
    // Find the video by ID
    const video = videos.find(v => v.id === videoId || v._id === videoId);
    
    if (!video) {
      throw new Error(`Video with ID ${videoId} not found`);
    }

    return video;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch videos with filters (pagination, section, status, etc.)
 * @param {Object} params - Query parameters
 * @returns {Promise<Array>} Array of video objects
 */
export const fetchVideos = async (params = {}) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    const url = `${API_BASE_URL}/videos?${queryString}`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const responseData = await response.json();
    
    // Handle wrapped response: { success, data: [...], pagination: {...} }
    const videos = Array.isArray(responseData) ? responseData : (responseData.data || []);
    return videos;
  } catch (error) {
    throw error;
  }
};

export default {
  fetchVideoByIdDirect,
  fetchVideoById,
  fetchVideos,
};
